import { useAtomValue, useSetAtom } from 'jotai';

import { SkeletonRect, Stack } from '@ekhodealer/ekho-common/rev';

import type { FiltersDrawerCheckboxGroupItem } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { FiltersDrawerCheckboxGroup } from '@common/components/FilterGroups/FiltersDrawerCheckboxGroup';

import { makeFilterAtom, updateMakeFilterAtom } from '../../../atoms/inventoryAtoms';
import { useInventoryFilterFacetCounts } from '../../../hooks/useInventoryFilterFacetCounts';

const LoadingCheckboxGroup = () => {
	return (
		<Stack itemsSpacing='8'>
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
			<SkeletonRect width='100%' height='24px' />
		</Stack>
	);
};

/**
 * VehicleMakeSection component that provides make/brand filtering options
 * Dynamically generates options based on available makes in inventory with counts
 * This is used within the VehicleInformationSection accordion
 */
export function VehicleMakeSection() {
	const makes = useAtomValue(makeFilterAtom);
	const setMakes = useSetAtom(updateMakeFilterAtom);

	// Get dynamic facet counts from the search response
	const { makeCounts, isLoading } = useInventoryFilterFacetCounts();

	// Build make items with dynamic counts from facets
	// Sort alphabetically for consistent ordering
	const makeItems: FiltersDrawerCheckboxGroupItem[] = Object.entries(makeCounts)
		.map(([make, count]) => ({
			value: make,
			label: make,
			count,
		}))
		.sort((a, b) => a.label.localeCompare(b.label));

	// Don't render if data has loaded but there are no options
	if (!isLoading && makeItems.length === 0) {
		return null;
	}

	return (
		<>
			{isLoading ? (
				<LoadingCheckboxGroup />
			) : (
				<FiltersDrawerCheckboxGroup
					label='Make'
					items={makeItems}
					value={makes}
					onChange={setMakes}
					tooltipHelpText='Make filter counts are based on matched inventory'
				/>
			)}
		</>
	);
}
